import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { getClientType } from '../../../tools/session/clientSession';
import { loadAnalysisDataset } from '../APIs/analysisData';

const DataContext = createContext(null);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children, clientType: clientTypeProp }) => {
  const clientType = clientTypeProp || getClientType();
  const [geojson, setGeojson] = useState(null);
  const [percentageData, setPercentageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extract zone names from GeoJSON
  const [zoneNames, setZoneNames] = useState([]);

  // Extract available day types and time slots from percentage data
  const [dayTypes, setDayTypes] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);

  // Client display config (map center, zoom, etc.)
  const [clientDisplayConfig, setClientDisplayConfig] = useState(null);
  const [dataClient, setDataClient] = useState(clientType);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    loadAnalysisDataset(clientType)
      .then(({ geojson: geojsonData, percentages: percentageDataRaw, config: configData, clientType: resolvedClient }) => {
        if (cancelled) return;
        setGeojson(geojsonData);
        setPercentageData(percentageDataRaw);
        setClientDisplayConfig(configData);
        setDataClient(resolvedClient);

        // Extract zone names from GeoJSON features
        const names = (geojsonData?.features || [])
          .map((feature) => feature.properties?.name)
          .filter(Boolean)
          .sort();
        setZoneNames([...new Set(names)]);

        // Extract day types and time slots from percentage data
        const types = Object.keys(percentageDataRaw || {});
        setDayTypes(types);
        setTimeSlots(types.length > 0
          ? ['Intera giornata', ...Object.keys(percentageDataRaw[types[0]]).sort()]
          : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error loading data:', err);
        setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clientType]);

  const clientConfig = useMemo(() => ({
    id: dataClient,
    ...(clientDisplayConfig || {}),
  }), [dataClient, clientDisplayConfig]);

  const value = {
    geojson,
    percentageData,
    zoneNames,
    dayTypes,
    timeSlots,
    loading,
    error,
    clientConfig,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export default DataContext;
